// This file is not yet translated.
/* eslint-disable i18next/no-literal-string */

import { SxProps } from "@outschool/backpack";
import {
  UrlParams,
  fuseQueryParams,
  queryStringToObject,
} from "@outschool/routes";
import { Location, LocationDescriptor } from "history";
import lodashOmit from "lodash/omit";
import lodashPick from "lodash/pick";
import React from "react";
import { NavLink, NavLinkProps, useLocation } from "react-router-dom";

import { useImpressionTracking } from "./tracking";
import { TrackingProperties } from "./TrackingProperties";

type LocationState = Record<string, any>;
type LS = LocationState;
type LinkDestination = NavLinkProps<LS>["to"];

export const PERSISTENT_QUERY_PARAMS = ["locale"];

export function addStateToLocation(
  location: LocationDescriptor<LS>,
  newState?: Record<string, any>,
  newQueryParams?: UrlParams,
): LocationDescriptor<LS>;
export function addStateToLocation(
  location: (loc: Location<LS>) => LocationDescriptor<LS>,
  newState?: Record<string, any>,
  newQueryParams?: UrlParams,
): (loc: Location<LS>) => LocationDescriptor<LS>;
export function addStateToLocation(
  location: LinkDestination,
  newState?: Record<string, any>,
  newQueryParams?: UrlParams,
): LinkDestination {
  if (!location) {
    return location;
  }
  if (typeof location === "string") {
    const [withoutHash, hash] = location.split("#");
    const [pathname, queryParams] = withoutHash.split("?");
    const search = newQueryParams
      ? fuseQueryParams(queryParams, newQueryParams)
      : queryParams;
    return {
      pathname,
      state: newState || {},
      ...(search ? { search } : {}),
      ...(hash ? { hash: `#${hash}` } : {}),
    };
  }
  if (typeof location === "object") {
    const search = newQueryParams
      ? fuseQueryParams(location.search, newQueryParams)
      : location.search;
    return {
      ...location,
      state: { ...(location.state || {}), ...newState },
      ...(search ? { search } : {}),
    };
  }
  if (typeof location === "function") {
    const locationFn = location as (
      loc: Location<LS>,
    ) => LocationDescriptor<LS>;
    const newLocationFunction: (loc: Location<LS>) => LocationDescriptor<LS> = (
      oldLocation: Location<LS>,
    ) => {
      return addStateToLocation(
        locationFn(oldLocation),
        newState,
        newQueryParams,
      );
    };
    return newLocationFunction;
  }
  return location;
}

export interface LinkProps extends NavLinkProps {
  /** True if the link should be disabled */
  disabled?: boolean;
  onClick?: (e: React.SyntheticEvent) => void;
  reloadDocument?: boolean;
  /** Additional properties to add to the tracking event */
  sharedProperties?: TrackingProperties;
  style?: React.CSSProperties;
  /**
   * Base name of the tracking event to be triggered when the link is clicked or viewed
   *
   * TODO: Make this required
   *
   * @see useImpressionTracking.trackingLabel for more information
   */
  trackingName?: string;
  /** Override the tracking event name for the touch event */
  trackingTouchEventName?: string;
  /** Override the tracking event name for the view event */
  trackingViewEventName?: string;
  /**
   * Unique identifier for the link (required to enable link tracking)
   * TODO: how should this be generated?
   * TODO: Make this required
   */
  trackingUniqueId?: string;
  /** TODO: What is this and how is it different from touch & view events? */
  useLinkTracking?: boolean;
  trackViews?: boolean;
  /** TODO: fix this typing */
  to: any;
  /** TODO: does this styling actually work? NavLink doesn't accept sx */
  sx?: SxProps;
}

/**
 * Link component that wraps NavLink to add standardized tracking and additional navigation functionality
 */
function Link(
  {
    children,
    disabled = false,
    onClick,
    reloadDocument = false,
    sharedProperties = {},
    style,
    target,
    to,
    trackingName,
    trackingTouchEventName,
    trackingUniqueId,
    trackingViewEventName,
    useLinkTracking = false,
    trackViews,
    ...props
  }: LinkProps,
  ref: React.ForwardedRef<unknown>,
) {
  // fixes TypeError: Cannot add property openInNewTab, object is not extensible
  sharedProperties = Object.assign({}, sharedProperties);
  if (target && target === "_blank") {
    sharedProperties.openInNewTab = true;
  } else {
    sharedProperties.openInNewTab = false;
  }
  const { search } = useLocation();
  const queryParams = queryStringToObject(search);

  const impressionNodeRef = React.useRef<HTMLAnchorElement | null>(null);
  // State is needed to trigger useImpressionTracking hook re-runs when the DOM node changes
  // (refs don't cause re-renders, but hooks need to re-execute when their dependencies change)
  const [nodeForTracking, setNodeForTracking] =
    React.useState<HTMLAnchorElement | null>(null);
  React.useImperativeHandle(ref, () => impressionNodeRef.current);

  const { trackTouch, impressionId } = useImpressionTracking({
    node: nodeForTracking,
    // If no trackingName is provided, useImpressionTracking will return a no-op trackTouch function
    trackingLabel: trackingName!,
    uniqueId: trackingUniqueId!,
    trackingEventName: trackingTouchEventName,
    impressionEventName: trackingViewEventName,
    sharedProperties,
    trackViews,
  });

  const builtTo = React.useMemo(() => {
    const newState =
      trackingName && useLinkTracking
        ? {
            linkTracking: {
              linkType: trackingName,
              linkImpressionId: impressionId,
            },
          }
        : {};
    const newQueryParams = lodashPick(queryParams, PERSISTENT_QUERY_PARAMS);
    return addStateToLocation(to, newState, newQueryParams);
  }, [to, trackingName, impressionId, useLinkTracking, queryParams]);

  return (
    <NavLink
      ref={node => {
        impressionNodeRef.current = node;
        setNodeForTracking(node);
      }}
      to={builtTo}
      target={target}
      style={style}
      onClick={e => {
        if (disabled) {
          e.preventDefault();
        } else {
          if (trackingName) {
            trackTouch();
          }
          if (typeof onClick === "function") {
            onClick(e);
          }
          if (reloadDocument) {
            e.metaKey = true;
            return false;
          }
        }
        return true;
      }}
      {...lodashOmit(props, [
        "trackingEventName",
        "impressionEventName",
        "data-testid",
        "ownerState",
      ])}
    >
      {children}
    </NavLink>
  );
}

export default React.forwardRef(Link);
